import React, { useState, useMemo, useEffect } from 'react';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import { 
  Mountain, Plus, Trash2, Sun, Moon, 
  ArrowDownCircle, AlertCircle, 
  TrendingDown, Table, Calendar, 
  Clock, MinusCircle, Sparkles, AlertTriangle,
  ChevronUp, ChevronDown
} from 'lucide-react';

/**
 * DEBT MOUNTAIN
 * Updated: Set document title and dynamically generated favicon from SVG.
 */

const STORAGE_KEY = 'debt_mountain_v18';
const THEME_KEY = 'debt_mountain_theme_v18';

const initialLoans = [
  { id: "L1", name: "Chase Sapphire Card", balance: 4850.00, rate: 24.99, minPayment: 145 },
  { id: "L2", name: "Auto Loan (SUV)", balance: 22400.00, rate: 5.25, minPayment: 460 },
  { id: "L3", name: "Federal Student Loan", balance: 31200.00, rate: 6.80, minPayment: 325 },
  { id: "L4", name: "Home Improvement Loan", balance: 8500.00, rate: 11.50, minPayment: 210 },
  { id: "L5", name: "Local Credit Union CC", balance: 1200.00, rate: 18.25, minPayment: 40 },
];

const formatCurrency = (val, forceDecimals = true) => {
  const num = Math.max(0, Number(val) || 0);
  const hasDecimals = num % 1 !== 0;
  
  return num.toLocaleString(undefined, {
    minimumFractionDigits: (forceDecimals || hasDecimals) ? 2 : 0,
    maximumFractionDigits: 2
  });
};

const formatRate = (val) => {
  const num = Number(val) || 0;
  return num.toFixed(1);
};

const App = () => {
  // Load data and last used view from localStorage
  const [loans, setLoans] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).loans : initialLoans;
  });
  const [monthlyBudget, setMonthlyBudget] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyBudget : 2500;
  });
  
  const [view, setView] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? (JSON.parse(saved).view || 'table') : 'table';
  });

  const [strategy, setStrategy] = useState('avalanche');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    return saved === null ? true : saved === 'true';
  });

  const [editingCell, setEditingCell] = useState({ id: null, field: null, value: '' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Update Document Title and Favicon
  useEffect(() => {
    document.title = "Debt Mountain | Payment Planner";

    // Set dynamic SVG Favicon
    const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel = 'icon';
    // SVG markup based on Lucide Mountain icon with indigo color
    const svgIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
      </svg>
    `.trim();
    link.href = `data:image/svg+xml,${encodeURIComponent(svgIcon)}`;
    document.getElementsByTagName('head')[0].appendChild(link);
  }, []);

  // Save changes to localStorage whenever core data or view changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ loans, monthlyBudget, view }));
    localStorage.setItem(THEME_KEY, isDarkMode.toString());
  }, [loans, monthlyBudget, isDarkMode, view]);

  const totalDebtBalance = useMemo(() => loans.reduce((sum, l) => sum + (Number(l.balance) || 0), 0), [loans]);
  const totalMinPayment = useMemo(() => loans.reduce((sum, l) => sum + (Number(l.minPayment) || 0), 0), [loans]);
  
  const sortedLoans = useMemo(() => {
    let sortableLoans = [...loans];
    if (sortConfig.key !== null) {
      sortableLoans.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (typeof aVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        aVal = (aVal || "").toString().toLowerCase();
        bVal = (bVal || "").toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableLoans;
  }, [loans, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleBudgetChange = (e) => {
    let raw = e.target.value;
    const cleanNumeric = raw.replace(/[^0-9.]/g, '');
    const parts = cleanNumeric.split('.');
    const sanitized = parts[0] + (parts.length > 1 ? '.' + parts[1].slice(0, 2) : '');
    setEditingCell(prev => ({ ...prev, value: sanitized }));
    const num = parseFloat(sanitized);
    if (!isNaN(num)) setMonthlyBudget(num);
  };

  const handleInputBlur = (id, field) => {
    const rawValue = editingCell.value.replace(/[^0-9.]/g, '');
    const finalValue = field === 'name' ? editingCell.value : parseFloat(rawValue) || 0;
    if (id === 'budget') {
      setMonthlyBudget(finalValue);
    } else {
      setLoans(prev => prev.map(l => l.id === id ? { ...l, [field]: finalValue } : l));
    }
    setEditingCell({ id: null, field: null, value: '' });
  };

  const handleFocus = (e, id, field, value) => {
    const cleanValue = field === 'name' ? value : value.toString();
    setEditingCell({ id, field, value: cleanValue });
    setTimeout(() => { if (e.target) e.target.select(); }, 0);
  };

  const projection = useMemo(() => {
    if (totalDebtBalance === 0 || monthlyBudget <= 0) return { history: [], totalMonths: 0, totalPaidInterest: 0 };
    
    let currentLoans = loans.map(l => ({ 
      id: l.id,
      name: l.name,
      balance: Math.max(0, Number(l.balance) || 0), 
      rate: Number(l.rate) || 0, 
      min: Number(l.minPayment) || 0,
      isFinished: false 
    }));

    const history = [];
    let month = 0;
    let totalPaidInterest = 0;
    const initialDate = new Date();

    history.push({
      name: initialDate.toLocaleString('default', { month: 'short', year: 'numeric' }),
      ...Object.fromEntries(currentLoans.map(l => [l.id, l.balance]))
    });
    
    while (currentLoans.some(l => l.balance > 0) && month < 600) { 
      month++;
      const date = new Date(); 
      date.setMonth(date.getMonth() + month);
      
      currentLoans.forEach(l => {
        if (l.balance > 0) {
          const monthlyRate = (l.rate / 100) / 12;
          const interest = l.balance * monthlyRate;
          l.balance += interest;
          totalPaidInterest += interest;
        }
      });

      let pool = monthlyBudget;
      currentLoans.forEach(l => {
        if (l.balance > 0) {
          const payment = Math.min(l.balance, l.min);
          l.balance -= payment;
          pool -= payment;
        }
      });

      if (pool > 0) {
        const targets = [...currentLoans]
          .filter(l => l.balance > 0)
          .sort((a, b) => strategy === 'avalanche' ? b.rate - a.rate : a.balance - b.balance);

        for (const target of targets) {
          const payment = Math.min(target.balance, pool);
          target.balance -= payment;
          pool -= payment;
          if (pool <= 0) break;
        }
      }

      const monthData = { name: date.toLocaleString('default', { month: 'short', year: 'numeric' }) };
      
      currentLoans.forEach(l => {
        if (l.balance < 0.01) l.balance = 0;
        if (l.balance === 0) {
          if (!l.isFinished) {
            monthData[l.id] = 0; 
            l.isFinished = true;
          } else {
            monthData[l.id] = null; 
          }
        } else {
          monthData[l.id] = parseFloat(l.balance.toFixed(2));
        }
      });
      
      history.push(monthData);
    }

    return { history, totalMonths: month, totalPaidInterest };
  }, [loans, monthlyBudget, strategy, totalDebtBalance]);

  const freedomDate = useMemo(() => {
    const d = new Date(); 
    d.setMonth(d.getMonth() + projection.totalMonths);
    return {
      month: d.toLocaleString('default', { month: 'long' }),
      year: d.getFullYear()
    };
  }, [projection.totalMonths]);

  const totalMinRequired = loans.reduce((sum, l) => sum + (Number(l.minPayment) || 0), 0);
  const isBelowMinimum = monthlyBudget < totalMinRequired;
  const surplusAmount = Math.max(0, monthlyBudget - totalMinRequired);
  const shortfallAmount = Math.max(0, totalMinRequired - monthlyBudget);

  const theme = {
    bg: isDarkMode ? 'bg-[#0b0f1a]' : 'bg-[#f8fafc]',
    card: isDarkMode ? 'bg-[#151b2b] border-slate-800' : 'bg-white border-slate-200',
    text: isDarkMode ? 'text-slate-200' : 'text-slate-800',
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp size={12} className="ml-1 inline" /> : <ChevronDown size={12} className="ml-1 inline" />;
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} font-sans transition-colors duration-300 pb-20`}>
      <div className="max-w-6xl mx-auto p-6 md:p-10 flex flex-col gap-8">
        
        <header className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-xl shadow-indigo-600/30">
              <Mountain size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-black font-serif italic tracking-tight leading-none text-indigo-500">Debt Mountain</h1>
              <p className="text-slate-500 text-[11px] font-bold tracking-[0.2em] uppercase mt-1">Find your way down</p>
            </div>
          </div>
          <button onClick={() => setIsDarkMode(!isDarkMode)} className={`h-12 w-12 flex items-center justify-center rounded-2xl border ${theme.card} hover:scale-105 transition-all`}>
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`${theme.card} p-5 rounded-3xl border shadow-sm`}>
            <div className="flex items-center gap-2 mb-1.5 text-slate-500">
               <ArrowDownCircle size={14} />
               <span className="text-[9px] font-black uppercase tracking-widest">Total Balance</span>
            </div>
            <p className="text-xl font-serif italic font-black">${formatCurrency(totalDebtBalance)}</p>
          </div>
          <div className={`${theme.card} p-5 rounded-3xl border shadow-sm`}>
            <div className="flex items-center gap-2 mb-1.5 text-slate-500">
               <AlertCircle size={14} />
               <span className="text-[9px] font-black uppercase tracking-widest">Min Payments</span>
            </div>
            <p className="text-xl font-serif italic font-black">${formatCurrency(totalMinPayment)}</p>
          </div>
          <div className={`${theme.card} p-5 rounded-3xl border shadow-sm`}>
            <div className="flex items-center gap-2 mb-1.5 text-indigo-400">
               <MinusCircle size={14} />
               <span className="text-[9px] font-black uppercase tracking-widest">Interest Payable</span>
            </div>
            <p className="text-xl font-serif italic font-black text-indigo-400">${formatCurrency(projection.totalPaidInterest)}</p>
          </div>
          <div className="bg-indigo-600 p-5 rounded-3xl shadow-xl shadow-indigo-600/20 text-white relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-1.5 opacity-80">
                 <Calendar size={14} />
                 <span className="text-[9px] font-black uppercase tracking-widest">Zero Balance</span>
              </div>
              <p className="text-xl font-serif italic font-black">
                {projection.totalMonths >= 600 ? "N/A" : `${freedomDate.month} ${freedomDate.year}`}
              </p>
              <div className="flex items-center gap-2 mt-1 opacity-70">
                <Clock size={11} />
                <p className="text-[9px] font-bold uppercase tracking-wider">{projection.totalMonths} months</p>
              </div>
            </div>
          </div>
        </section>

        <section className={`${theme.card} rounded-[2rem] p-6 md:px-10 md:py-6 border shadow-lg overflow-hidden`}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Monthly Payment Plan</span>
              <div className="flex items-center gap-4 flex-wrap">
                <div className={`flex items-baseline gap-1.5 transition-colors duration-300 ${isBelowMinimum ? 'text-rose-500' : 'text-indigo-600'}`}>
                  <span className="text-3xl font-serif italic font-black">$</span>
                  <input 
                    type="text" 
                    className="bg-transparent text-3xl font-serif italic font-black outline-none border-b-2 border-transparent focus:border-current transition-all placeholder-slate-300 w-auto min-w-[100px]"
                    style={{ width: `${(editingCell.id === 'budget' ? editingCell.value.length : formatCurrency(monthlyBudget, false).length) + 1}ch` }}
                    value={editingCell.id === 'budget' ? editingCell.value : formatCurrency(monthlyBudget, false)}
                    onFocus={(e) => handleFocus(e, 'budget', 'budget', monthlyBudget)}
                    onBlur={() => handleInputBlur('budget', 'budget')}
                    onChange={handleBudgetChange}
                    spellCheck="false"
                  />
                </div>
                <div className="flex items-center">
                  {isBelowMinimum ? (
                    <div className="flex items-center gap-2 px-3 py-1 bg-rose-500/10 border border-rose-500/20 rounded-full text-rose-500">
                      <AlertTriangle size={12} />
                      <span className="text-[10px] font-black uppercase tracking-widest">${formatCurrency(shortfallAmount)} Shortfall</span>
                    </div>
                  ) : surplusAmount > 0 ? (
                    <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-500/80">
                      <Sparkles size={12} />
                      <span className="text-[10px] font-black uppercase tracking-widest">${formatCurrency(surplusAmount)} Surplus</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="w-full">
              <input 
                type="range" 
                min={0} max={10000} step={1}
                value={Math.round(monthlyBudget)}
                onChange={(e) => setMonthlyBudget(parseFloat(e.target.value))}
                className={`w-full h-1.5 rounded-full appearance-none cursor-pointer transition-colors duration-300 ${isBelowMinimum ? 'bg-rose-500/20 accent-rose-500' : 'bg-slate-800/10 dark:bg-slate-800/50 accent-indigo-600'} minimal-slider`}
              />
            </div>
          </div>
        </section>

        <div className={`${theme.card} rounded-[2rem] border overflow-hidden shadow-2xl min-h-[450px] flex flex-col`}>
          <div className="flex items-center border-b border-inherit">
             <button 
                onClick={() => setView('table')}
                className={`flex-1 py-4 flex items-center justify-center gap-3 transition-all ${view === 'table' ? 'bg-indigo-600/5 text-indigo-500 border-b-2 border-indigo-500' : 'text-slate-500 hover:text-slate-300'}`}
             >
                <Table size={14} />
                <span className="text-[10px] font-black uppercase tracking-widest">Ledger</span>
             </button>
             <button 
                onClick={() => setView('graph')}
                className={`flex-1 py-4 flex items-center justify-center gap-3 transition-all ${view === 'graph' ? 'bg-indigo-600/5 text-indigo-500 border-b-2 border-indigo-500' : 'text-slate-500 hover:text-slate-300'}`}
             >
                <TrendingDown size={14} />
                <span className="text-[10px] font-black uppercase tracking-widest">Projection Graph</span>
             </button>
          </div>

          <div className="p-6 md:p-8 flex-1">
            {view === 'table' ? (
              <div className="flex flex-col">
                <div className="flex items-end mb-6 px-2 flex-wrap gap-4">
                   <button 
                    onClick={() => setLoans(prev => [{id: `L${Date.now()}`, name: "New Debt", balance: 0, rate: 0, minPayment: 0}, ...prev])}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl font-bold text-[9px] uppercase tracking-widest shadow-lg transition-all"
                  >
                    <Plus size={12} /> Add Debt
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[8px] font-black uppercase tracking-[0.2em] text-slate-400 border-b border-inherit">
                        <th className="py-4 pl-2 cursor-pointer" onClick={() => requestSort('name')}>Debt <SortIcon column="name" /></th>
                        <th className="py-4 cursor-pointer" onClick={() => requestSort('balance')}>Balance <SortIcon column="balance" /></th>
                        <th className="py-4 cursor-pointer" onClick={() => requestSort('rate')}>Rate <SortIcon column="rate" /></th>
                        <th className="py-4 cursor-pointer" onClick={() => requestSort('minPayment')}>Min Payment <SortIcon column="minPayment" /></th>
                        <th className="py-4 w-[5%] text-right"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-inherit">
                      {sortedLoans.map(loan => (
                        <tr key={loan.id} className="group hover:bg-indigo-500/5">
                          <td className="py-3 pl-2">
                            <input className="bg-transparent font-bold text-sm outline-none w-full" value={editingCell.id === loan.id && editingCell.field === 'name' ? editingCell.value : loan.name} onFocus={(e) => handleFocus(e, loan.id, 'name', loan.name)} onBlur={() => handleInputBlur(loan.id, 'name')} onChange={(e) => setEditingCell({...editingCell, value: e.target.value})} />
                          </td>
                          <td className="py-3">
                            <input className="bg-transparent font-bold text-sm outline-none w-full" value={editingCell.id === loan.id && editingCell.field === 'balance' ? editingCell.value : `$${formatCurrency(loan.balance)}`} onFocus={(e) => handleFocus(e, loan.id, 'balance', loan.balance)} onBlur={() => handleInputBlur(loan.id, 'balance')} onChange={(e) => setEditingCell({...editingCell, value: e.target.value})} />
                          </td>
                          <td className="py-3">
                             <input className="bg-transparent font-bold text-sm outline-none w-full text-indigo-500" value={editingCell.id === loan.id && editingCell.field === 'rate' ? editingCell.value : `${formatRate(loan.rate)}%`} onFocus={(e) => handleFocus(e, loan.id, 'rate', loan.rate)} onBlur={() => handleInputBlur(loan.id, 'rate')} onChange={(e) => setEditingCell({...editingCell, value: e.target.value})} />
                          </td>
                          <td className="py-3">
                             <input className="bg-transparent font-bold text-sm outline-none w-full" value={editingCell.id === loan.id && editingCell.field === 'minPayment' ? editingCell.value : `$${formatCurrency(loan.minPayment)}`} onFocus={(e) => handleFocus(e, loan.id, 'minPayment', loan.minPayment)} onBlur={() => handleInputBlur(loan.id, 'minPayment')} onChange={(e) => setEditingCell({...editingCell, value: e.target.value})} />
                          </td>
                          <td className="py-3 text-right pr-2">
                            <button onClick={() => setLoans(prev => prev.filter(l => l.id !== loan.id))} className="text-slate-600 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="h-[400px] w-full flex flex-col gap-4">
                <div className="flex items-center justify-between mb-2">
                   <div className="flex gap-4">
                      <button onClick={() => setStrategy('avalanche')} className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all ${strategy === 'avalanche' ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-800 text-slate-500'}`}>Avalanche</button>
                      <button onClick={() => setStrategy('snowball')} className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all ${strategy === 'snowball' ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-800 text-slate-500'}`}>Snowball</button>
                   </div>
                </div>
                <div className="flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={projection.history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        {loans.map((l, i) => (
                          <linearGradient key={`grad-${l.id}`} id={`color-${l.id}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={`hsl(${220 + (i * 45)}, 70%, 60%)`} stopOpacity={0.4}/>
                            <stop offset="95%" stopColor={`hsl(${220 + (i * 45)}, 70%, 60%)`} stopOpacity={0}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#1e293b' : '#e2e8f0'} />
                      <XAxis dataKey="name" tick={{fontSize: 9, fontWeight: 700, fill: '#64748b'}} axisLine={false} tickLine={false} dy={10} minTickGap={45} />
                      <YAxis tickFormatter={v => `$${v.toLocaleString()}`} tick={{fontSize: 10, fontWeight: 700, fill: '#64748b'}} axisLine={false} tickLine={false} dx={-10} />
                      <RechartsTooltip 
                        contentStyle={{ borderRadius: '16px', border: 'none', backgroundColor: '#0f172a', color: '#fff', fontSize: '11px' }}
                        itemStyle={{ padding: '2px 0' }}
                        formatter={(value, name) => value === null ? null : [`$${formatCurrency(value)}`, loans.find(l => l.id === name)?.name || name]}
                      />
                      {loans.map((l, i) => (
                        <Area 
                          key={l.id} 
                          type="monotone" 
                          dataKey={l.id} 
                          name={l.name} 
                          stroke={`hsl(${220 + (i * 45)}, 70%, 60%)`} 
                          fill={`url(#color-${l.id})`} 
                          strokeWidth={2} 
                          isAnimationActive={false} 
                          connectNulls={false}
                          fillOpacity={0.6}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Inter:wght@400;700;900&display=swap');
        .font-serif { font-family: 'Libre Baskerville', serif; }
        body { font-family: 'Inter', sans-serif; }
        .minimal-slider::-webkit-slider-thumb {
          appearance: none; width: 24px; height: 24px; border-radius: 50%;
          background: currentColor; border: 3px solid white; cursor: pointer;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .minimal-slider::-moz-range-thumb {
          width: 24px; height: 24px; border-radius: 50%;
          background: currentColor; border: 3px solid white; cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default App;